import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { ReactTable } from "../../SimpleForm/ReactTable";
import SimpleModal from "../../SimpleModal";
import Link790AAddendumBColumns from "../columns/790A/Selection790AAddendumBColumns";
import { saveLinkages } from "../utils";
import Button from "../../SimpleForm/UI/Button";

const ModalBody = styled.div`
  padding: 1rem;
  z-index: 9999;
`;
const ModalActionToolbar = styled.div`
  display: flex;
  justify-content: "flex-end";
  padding: 1rem;
  .button-container {
    margin-left: auto;
  }
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  .highlight {
    background: transparent;
    border: none;
    color: #1076be;
  }
  .clear-button {
    background: transparent;
    border: none;
    margin-left: 12px;
  }
`;

export const LinkageSelectionTable = ({
  linkageIdList = [],
  formApi,
  setLinkageIdList,
  saveRowIndex, // required to save directly from inline table
  setLinkageEditMode, // required to rerender inline table
  isOpen,
  setIsOpen,
  schemaProps: {
    label,
    machine_name,
    tableInfoHeader,
    linkages: {
      fromFieldArray,
      toFieldArray,
      idFieldName,
      selectionTableColumn,
    },
  },
}) => {
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(linkageIdList || []);
  const [data, setData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [paginationProps, setPaginationProps] = useState({
    pageIndex: 0,
    pageSize: 0,
  });

  const formValues = formApi.getState().values;

  // Set linkable data
  useEffect(() => {
    // If array already exists
    if (fromFieldArray && !!formValues?.[fromFieldArray]?.length) {
      // Fetch data
      setData(formValues?.[fromFieldArray]);
    }
  }, []);

  //Set initial selection data + count
  useEffect(() => {
    // Set selected based on current linked crops
    setSelectedIds(linkageIdList);
  }, [isOpen]);

  // Set pagination properties
  useEffect(() => {
    const { pageIndex, pageSize } = paginationProps;

    if (typeof pageIndex === "number" && typeof pageSize === "number") {
      const startingIndex = pageIndex * pageSize;
      const endingIndex =
        Math.min((pageIndex + 1) * pageSize, data?.length) || data?.length;
      const newPaginatedData = data
        ?.slice(startingIndex, endingIndex)
        ?.map((item) => item?.[idFieldName]);

      setPaginatedData(newPaginatedData);
    }
  }, [data, paginationProps]);

  // Track if all items on page are checked
  useEffect(() => {
    let isAllSelected = false;
    if (!!paginatedData?.length) {
      isAllSelected = paginatedData?.every((item) =>
        selectedIds?.includes(item)
      );
    }
    setAllSelected(isAllSelected);
  }, [paginatedData, selectedIds]);

  // For now, track selected items in this manner for performance, until we update and fix tooltips implementation. Later, we can track row selection state thru object
  const toggleSelectAll = () => {
    if (allSelected) {
      const filteredSelected = selectedIds?.filter(
        (item) => !paginatedData?.includes(item)
      );
      setSelectedIds(filteredSelected);
    } else {
      const newSelected = [];
      paginatedData?.forEach(
        (item) => !selectedIds?.includes(item) && newSelected?.push(item)
      );
      setSelectedIds([...selectedIds, ...newSelected]);
    }

    setAllSelected(!allSelected);
  };

  const closeModal = () => {
    setIsOpen(false);
    setLinkageEditMode && setLinkageEditMode(false);
  };

  const columns = (selectionTableColumn) => {
    const lookup = {
      Selection790AAddendumBColumns: Link790AAddendumBColumns({
        selectedIds,
        setSelectedIds,
        toggleSelectAll,
        allSelected,
      }),
    };
    return lookup[selectionTableColumn] ?? [{}];
  };

  return (
    <SimpleModal
      toggleModal={() => {
        closeModal();
      }}
      modalStatus={isOpen}
      renderButton={() => null}
      minWidth="100%"
    >
      <div className="linkage-selection-modal">
        <ModalBody>
          <h3>{label}</h3>
          <TableHeader>
            <i style={{ marginBottom: 16 }}>{tableInfoHeader}</i>
            {!!selectedIds?.length && (
              <div>
                <span className="highlight">{selectedIds?.length}</span> of{" "}
                <span>{data?.length}</span> rows selected
                <button
                  className="clear-button highlight"
                  type="button"
                  onClick={() => setSelectedIds([])}
                >
                  Clear all
                </button>
              </div>
            )}
          </TableHeader>
          <ReactTable
            // showRowNumbers
            columns={columns(selectionTableColumn)}
            tableTheme="casesTableTheme"
            data={data}
            pageSizeOptions={[10, 25, 50, 100]}
            showPagination={!!data ? data.length > 5 : false}
            defaultSortBy={[
              {
                id: idFieldName,
                desc: false,
              },
            ]}
          />
        </ModalBody>
        <ModalActionToolbar>
          <div className="button-container">
            <Button
              coloring="cancel"
              type="button"
              onClick={() => closeModal()}
            >
              Cancel
            </Button>
            <Button
              coloring="blue"
              type="button"
              onClick={() => {
                saveLinkages({
                  selectedData: selectedIds,
                  machine_name,
                  formApi,
                  saveRowIndex,
                  toFieldArray,
                });
                setLinkageIdList(selectedIds);
                setLinkageEditMode && setLinkageEditMode(false);
                setIsOpen(false);
              }}
            >
              Save
            </Button>
          </div>
        </ModalActionToolbar>
      </div>
    </SimpleModal>
  );
};
